import React, { useEffect, useMemo, useState } from "react";
import { Button, Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { publicApi } from "../../api/http";
import ChartShareModal from "../../components/ChartShareModal/ChartShareModal";
import PageLoader from "../../components/PageLoader/PageLoader";
import "./DraftDebuts.css";

const AXIS_COLOR = "rgba(243, 244, 246, 0.72)";
const GRID_COLOR = "rgba(255, 255, 255, 0.08)";
const BAR_FILL = "rgba(126, 168, 98, 0.85)";
// Room for tick labels only — axis titles sit outside the SVG via CSS.
const CHART_MARGIN = { top: 12, right: 20, left: 8, bottom: 8 };
const Y_AXIS_WIDTH = 44;

const SHARE_CHARTS = {
    "by-years": {
        title: "Share debut timing chart",
        description: "Share the NHL debut rate by years since draft.",
        fileName: "nhl-debut-by-years.png",
    },
    "by-years-round": {
        title: "Share debut-by-round timing chart",
        description: "Share NHL debut rates by years since draft and draft round.",
        fileName: "nhl-debut-by-years-round.png",
    },
    "debut-by-round": {
        title: "Share NHL debut probability chart",
        description: "Share the probability of an NHL debut by draft round.",
        fileName: "nhl-debut-by-round.png",
    },
    "by-round-gp": {
        title: "Share 100+ GP chart",
        description: "Share the probability of reaching 100+ NHL games by draft round.",
        fileName: "nhl-debut-100-plus-gp.png",
    },
};
const ROUND_COLORS = {
    R1: "#7ea862",
    R2: "#5b9bd5",
    R3: "#e8a838",
    R4: "#c97b84",
    R5: "#9b7ed9",
    R6: "#4db6ac",
    R7: "#f0a06a",
    "R8+": "#8a93a3",
    Unknown: "#6b7280",
};

const DEFAULT_ROUNDS = ["R1", "R2", "R3", "R4", "R5", "R6", "R7"];

function formatPct(value) {
    if (value == null || Number.isNaN(value)) return "—";
    return `${Number(value).toFixed(1)}%`;
}

function formatInt(value) {
    if (value == null) return "—";
    return Number(value).toLocaleString();
}

function ChartTooltip({ active, payload, label, valueSuffix = "%" }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="draft-debuts-tooltip">
            <div className="draft-debuts-tooltip-title">{label}</div>
            {payload.map((entry) => (
                <div key={entry.dataKey} className="draft-debuts-tooltip-row">
                    <span style={{ color: entry.color || BAR_FILL }}>{entry.name}</span>
                    <strong>
                        {typeof entry.value === "number" ? entry.value.toFixed(1) : entry.value}
                        {valueSuffix}
                    </strong>
                </div>
            ))}
        </div>
    );
}

const CHART_TOOLTIP_PROPS = {
    content: <ChartTooltip />,
    cursor: { fill: "rgba(126, 168, 98, 0.12)", stroke: "rgba(148, 192, 115, 0.25)" },
    wrapperStyle: { outline: "none", zIndex: 2 },
};

function MetricCard({ label, value }) {
    return (
        <div className="draft-debuts-metric">
            <span className="draft-debuts-metric-label">{label}</span>
            <span className="draft-debuts-metric-value">{value}</span>
        </div>
    );
}

function ChartFrame({ xLabel, yLabel, height, children }) {
    return (
        <div className="draft-debuts-chart">
            <div className="draft-debuts-chart-frame" style={{ "--chart-height": `${height}px` }}>
                {yLabel ? (
                    <span className="draft-debuts-axis-label draft-debuts-axis-label--y">{yLabel}</span>
                ) : null}
                <div className="draft-debuts-chart-plot">{children}</div>
            </div>
            {xLabel ? (
                <span className="draft-debuts-axis-label draft-debuts-axis-label--x">{xLabel}</span>
            ) : null}
        </div>
    );
}

function SectionHeader({ title, shareChartId, onShare }) {
    return (
        <div className="draft-debuts-section-header">
            <Typography className="draft-debuts-section-title" component="h2">
                {title}
            </Typography>
            {shareChartId ? (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareOutlinedIcon />}
                    className="draft-debuts-share-btn"
                    onClick={() => onShare(shareChartId)}
                >
                    Share
                </Button>
            ) : null}
        </div>
    );
}

function DraftDebuts() {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRounds, setSelectedRounds] = useState(DEFAULT_ROUNDS);
    const [shareChartId, setShareChartId] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        publicApi
            .get("/draft_debut_actuals")
            .then((response) => {
                setPayload(response.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching draft debut actuals:", err);
                setPayload(null);
                setError(
                    err?.response?.status === 404
                        ? "Debut timing data has not been published yet."
                        : "Unable to load debut timing data."
                );
                setLoading(false);
            });
    }, []);

    const meta = payload?.meta || {};
    const byYears = payload?.by_years || [];
    const byYearsRound = payload?.by_years_round || [];
    const byRoundGp = payload?.by_round_gp || [];
    const minCareerGp = meta.min_career_gp || 100;

    // Inferred from payload meta — not recomputed in the workflow.
    const neverNhl = useMemo(() => {
        const cohort = Number(meta.cohort_size);
        const debuted = Number(meta.debuted_within_window);
        if (!Number.isFinite(cohort) || cohort <= 0 || !Number.isFinite(debuted)) {
            return { count: null, pct: null };
        }
        const count = Math.max(0, cohort - debuted);
        return { count, pct: (100 * count) / cohort };
    }, [meta.cohort_size, meta.debuted_within_window]);

    const neverMinGp = useMemo(() => {
        const cohort = Number(meta.gp_cohort_size);
        const reached = Number(meta.gp_reached);
        if (!Number.isFinite(cohort) || cohort <= 0 || !Number.isFinite(reached)) {
            return { count: null, pct: null };
        }
        const count = Math.max(0, cohort - reached);
        return { count, pct: (100 * count) / cohort };
    }, [meta.gp_cohort_size, meta.gp_reached]);

    const availableRounds = useMemo(() => {
        const seen = new Set(byYearsRound.map((row) => row.round_label));
        return Object.keys(ROUND_COLORS).filter((round) => seen.has(round));
    }, [byYearsRound]);

    const roundChartData = useMemo(() => {
        const byYear = new Map();
        byYearsRound.forEach((row) => {
            if (!selectedRounds.includes(row.round_label)) return;
            const key = row.years_since_draft;
            if (!byYear.has(key)) {
                byYear.set(key, { years_since_draft: key });
            }
            byYear.get(key)[row.round_label] = row.pct_of_round;
        });
        return Array.from(byYear.values()).sort(
            (a, b) => a.years_since_draft - b.years_since_draft
        );
    }, [byYearsRound, selectedRounds]);

    // P(NHL debut within window | round) = sum of per-year debut shares for that round.
    const debutByRound = useMemo(() => {
        const byRound = new Map();
        byYearsRound.forEach((row) => {
            const key = row.round_label;
            if (!byRound.has(key)) {
                byRound.set(key, {
                    round_label: key,
                    drafted: Number(row.drafted_in_round) || 0,
                    debuts: 0,
                });
            }
            byRound.get(key).debuts += Number(row.debuts) || 0;
        });
        return Object.keys(ROUND_COLORS)
            .filter((round) => byRound.has(round))
            .map((round) => {
                const row = byRound.get(round);
                const drafted = row.drafted;
                const debuts = row.debuts;
                const never = Math.max(0, drafted - debuts);
                return {
                    round_label: round,
                    drafted,
                    debuts,
                    never,
                    pct_debuted: drafted ? (100 * debuts) / drafted : 0,
                    pct_never: drafted ? (100 * never) / drafted : 0,
                };
            });
    }, [byYearsRound]);

    const toggleRound = (round) => {
        setSelectedRounds((current) => {
            if (current.includes(round)) {
                if (current.length <= 1) return current;
                return current.filter((item) => item !== round);
            }
            return [...current, round];
        });
    };

    const shareMeta = shareChartId ? SHARE_CHARTS[shareChartId] : null;

    if (loading) {
        return <PageLoader />;
    }

    return (
        <Container maxWidth="lg" className="draft-debuts-page">
            <Helmet>
                <title>NHL Debut Timing - Hockey Pipeline</title>
            </Helmet>

            <section className="home-panel draft-debuts-hero">
                <Typography className="draft-debuts-eyebrow" component="p">
                    Historical actuals
                </Typography>
                <Typography className="draft-debuts-page-title" component="h1">
                    NHL Debut Timing
                </Typography>
                <Typography className="draft-debuts-page-subtitle" component="p">
                    How long after the draft players typically reach the NHL, by overall cohort
                    and draft round — plus the chance of an NHL debut and of reaching 100+
                    career NHL games by round. Rates are precomputed offline (workflow) so this
                    page loads quickly.
                </Typography>
                {meta.computed_at && (
                    <Typography className="draft-debuts-updated" component="p">
                        Last updated{" "}
                        {new Date(meta.computed_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                        })}
                    </Typography>
                )}
            </section>

            {error ? (
                <section className="home-panel draft-debuts-empty">
                    <Typography component="p">{error}</Typography>
                </section>
            ) : (
                <>
                    <section className="home-panel draft-debuts-section">
                        <SectionHeader
                            title="1. Debut rate by years since draft"
                            shareChartId="by-years"
                            onShare={setShareChartId}
                        />
                        <Typography className="draft-debuts-section-caption" component="p">
                            Percent of drafted players whose first NHL season was exactly X years
                            after their draft year. Only draft classes with full follow-up through{" "}
                            {meta.max_offset ?? 10} years are included. “Never reached NHL” is the
                            share with no debut in that window (
                            {formatInt(neverNhl.count)} players).
                        </Typography>

                        <div className="draft-debuts-metrics">
                            <MetricCard label="Drafted players" value={formatInt(meta.cohort_size)} />
                            <MetricCard
                                label="Debuted within window"
                                value={formatInt(meta.debuted_within_window)}
                            />
                            <MetricCard
                                label="Never reached NHL"
                                value={formatPct(neverNhl.pct)}
                            />
                            <MetricCard
                                label="Draft years"
                                value={
                                    meta.draft_year_min != null
                                        ? `${meta.draft_year_min}–${meta.draft_year_max}`
                                        : "—"
                                }
                            />
                        </div>

                        <ChartFrame xLabel="Years since draft" yLabel="% of drafted" height={320}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={byYears} margin={CHART_MARGIN}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                                    <XAxis
                                        dataKey="years_since_draft"
                                        type="category"
                                        interval={0}
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickMargin={8}
                                    />
                                    <YAxis
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickFormatter={(v) => `${v}%`}
                                        width={Y_AXIS_WIDTH}
                                        tickMargin={6}
                                    />
                                    <Tooltip {...CHART_TOOLTIP_PROPS} />
                                    <Bar
                                        dataKey="pct_of_drafted"
                                        name="% of drafted"
                                        fill={BAR_FILL}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartFrame>

                        <div className="draft-debuts-table-wrap">
                            <table className="draft-debuts-table">
                                <thead>
                                    <tr>
                                        <th>Years since draft</th>
                                        <th>Debuts</th>
                                        <th>Cohort size</th>
                                        <th>% of drafted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byYears.map((row) => (
                                        <tr key={row.years_since_draft}>
                                            <td>{row.years_since_draft}</td>
                                            <td>{formatInt(row.debuts)}</td>
                                            <td>{formatInt(row.drafted_players)}</td>
                                            <td>{formatPct(row.pct_of_drafted)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="home-panel draft-debuts-section">
                        <SectionHeader
                            title="2. Debut rate by years since draft × draft round"
                            shareChartId="by-years-round"
                            onShare={setShareChartId}
                        />
                        <Typography className="draft-debuts-section-caption" component="p">
                            Same definition, with each round using its own denominator.
                        </Typography>

                        <div className="draft-debuts-round-toggles" role="group" aria-label="Rounds">
                            {availableRounds.map((round) => {
                                const active = selectedRounds.includes(round);
                                return (
                                    <button
                                        key={round}
                                        type="button"
                                        className={`draft-debuts-round-chip${active ? " draft-debuts-round-chip--active" : ""}`}
                                        style={{
                                            borderColor: active
                                                ? ROUND_COLORS[round]
                                                : "var(--border-subtle)",
                                            color: active ? ROUND_COLORS[round] : undefined,
                                        }}
                                        onClick={() => toggleRound(round)}
                                    >
                                        {round}
                                    </button>
                                );
                            })}
                        </div>

                        <ChartFrame xLabel="Years since draft" yLabel="% of round" height={360}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={roundChartData}
                                    margin={{ ...CHART_MARGIN, top: 8, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                                    <XAxis
                                        dataKey="years_since_draft"
                                        type="category"
                                        allowDuplicatedCategory={false}
                                        interval={0}
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickMargin={8}
                                        padding={{ left: 16, right: 16 }}
                                    />
                                    <YAxis
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickFormatter={(v) => `${v}%`}
                                        width={Y_AXIS_WIDTH}
                                        tickMargin={6}
                                    />
                                    <Tooltip {...CHART_TOOLTIP_PROPS} />
                                    <Legend
                                        verticalAlign="top"
                                        height={36}
                                        wrapperStyle={{ paddingBottom: 4 }}
                                    />
                                    {selectedRounds.map((round) => (
                                        <Line
                                            key={round}
                                            type="monotone"
                                            dataKey={round}
                                            name={round}
                                            stroke={ROUND_COLORS[round] || "#94a3b8"}
                                            strokeWidth={2}
                                            dot={false}
                                            connectNulls
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartFrame>
                    </section>

                    <section className="home-panel draft-debuts-section">
                        <SectionHeader
                            title="3. Probability of NHL debut by draft round"
                            shareChartId="debut-by-round"
                            onShare={setShareChartId}
                        />
                        <Typography className="draft-debuts-section-caption" component="p">
                            Percent of drafted players in each round who played at least one NHL
                            regular-season game within {meta.max_offset ?? 10} years of being
                            drafted. Inferred by summing the per-year debut rates from chart 2.
                        </Typography>

                        <ChartFrame
                            xLabel="Draft round"
                            yLabel="% with NHL debut"
                            height={320}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={debutByRound} margin={CHART_MARGIN}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                                    <XAxis
                                        dataKey="round_label"
                                        type="category"
                                        interval={0}
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickMargin={8}
                                    />
                                    <YAxis
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickFormatter={(v) => `${v}%`}
                                        width={Y_AXIS_WIDTH}
                                        tickMargin={6}
                                    />
                                    <Tooltip {...CHART_TOOLTIP_PROPS} />
                                    <Bar
                                        dataKey="pct_debuted"
                                        name="% with NHL debut"
                                        fill={BAR_FILL}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartFrame>

                        <div className="draft-debuts-table-wrap">
                            <table className="draft-debuts-table">
                                <thead>
                                    <tr>
                                        <th>Round</th>
                                        <th>Drafted</th>
                                        <th>NHL debut</th>
                                        <th>Never debuted</th>
                                        <th>% debuted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {debutByRound.map((row) => (
                                        <tr key={row.round_label}>
                                            <td>{row.round_label}</td>
                                            <td>{formatInt(row.drafted)}</td>
                                            <td>{formatInt(row.debuts)}</td>
                                            <td>{formatInt(row.never)}</td>
                                            <td>{formatPct(row.pct_debuted)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="home-panel draft-debuts-section">
                        <SectionHeader
                            title={`4. Probability of ${minCareerGp}+ NHL games by draft round`}
                            shareChartId="by-round-gp"
                            onShare={setShareChartId}
                        />
                        <Typography className="draft-debuts-section-caption" component="p">
                            Percent of drafted players in each round with career regular-season NHL
                            GP ≥ {minCareerGp}. Players drafted in the last{" "}
                            {meta.exclude_recent_draft_years || 5} years are excluded so incomplete
                            careers don’t skew rates.
                        </Typography>

                        <div className="draft-debuts-metrics">
                            <MetricCard
                                label={`Players (excl. last ${meta.exclude_recent_draft_years || 5} drafts)`}
                                value={formatInt(meta.gp_cohort_size)}
                            />
                            <MetricCard
                                label={`Reached ${minCareerGp}+ GP`}
                                value={formatInt(meta.gp_reached)}
                            />
                            <MetricCard
                                label={`Never reached ${minCareerGp}+ GP`}
                                value={
                                    neverMinGp.count == null
                                        ? "—"
                                        : `${formatInt(neverMinGp.count)} (${formatPct(neverMinGp.pct)})`
                                }
                            />
                            <MetricCard
                                label="Latest draft year included"
                                value={meta.gp_max_draft_year ?? "—"}
                            />
                        </div>

                        <ChartFrame
                            xLabel="Draft round"
                            yLabel={`% with ${minCareerGp}+ GP`}
                            height={320}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={byRoundGp} margin={CHART_MARGIN}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                                    <XAxis
                                        dataKey="round_label"
                                        type="category"
                                        interval={0}
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickMargin={8}
                                    />
                                    <YAxis
                                        stroke={AXIS_COLOR}
                                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                                        tickLine={{ stroke: AXIS_COLOR }}
                                        axisLine={{ stroke: AXIS_COLOR }}
                                        tickFormatter={(v) => `${v}%`}
                                        width={Y_AXIS_WIDTH}
                                        tickMargin={6}
                                    />
                                    <Tooltip {...CHART_TOOLTIP_PROPS} />
                                    <Bar
                                        dataKey="pct_reached"
                                        name={`% with ${minCareerGp}+ NHL GP`}
                                        fill={BAR_FILL}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartFrame>

                        <div className="draft-debuts-table-wrap">
                            <table className="draft-debuts-table">
                                <thead>
                                    <tr>
                                        <th>Round</th>
                                        <th>Drafted</th>
                                        <th>Reached {minCareerGp}+ GP</th>
                                        <th>Never {minCareerGp}+</th>
                                        <th>% of round</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byRoundGp.map((row) => (
                                        <tr key={row.round_label}>
                                            <td>{row.round_label}</td>
                                            <td>{formatInt(row.drafted)}</td>
                                            <td>{formatInt(row.reached)}</td>
                                            <td>{formatInt(Math.max(0, (row.drafted || 0) - (row.reached || 0)))}</td>
                                            <td>{formatPct(row.pct_reached)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            <ChartShareModal
                open={Boolean(shareChartId)}
                onClose={() => setShareChartId(null)}
                cardPath={shareChartId ? `/share/draft-debuts/${shareChartId}/card` : ""}
                sharePath={shareChartId ? `/share/draft-debuts/${shareChartId}` : ""}
                title={shareMeta?.title}
                description={shareMeta?.description}
                fileName={shareMeta?.fileName}
            />
        </Container>
    );
}

export default DraftDebuts;
